import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import TemplateFile from "../../../interfaces/task/TaskFiles/TemplateFile";
import { useAuth } from "../../../firebase/AuthProvider";
import { updateFileTemplate, deleteFileById } from "../../../services/fileServices/FileService";
import { Button } from "react-bootstrap";
import { GetFormReferences } from "../../../services/formsService/FormsService";
import { Form } from "../../../interfaces/dynamicForms/Forms";
import backarrowbtn from '../../../assets/images/backarrowbtn.svg';
import { toast } from "react-toastify";

interface EditTemplateInterimTaskProps {
  template: TemplateFile;
  setSelectedTemplate: (value: TemplateFile | undefined) => void;
  onSaveSuccess: () => void;
}
interface OptionType {
  value: string;
  label: string;
}

const EditTemplateFile: React.FC<EditTemplateInterimTaskProps> = ({ template, setSelectedTemplate, onSaveSuccess }) => {
  const { currentUser } = useAuth();
  const [formOptions, setFormOptions] = useState<OptionType[]>([]);

  const [newTemplate, setNewTemplate] = useState<TemplateFile>(template);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setNewTemplate(template);
  }, [template])

  useEffect(() => {
    const fetchForms = async () => {
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                const response = await GetFormReferences(token);
                if (response) { 
                    const options = response.map((form: Form) => ({
                        value: form.id.toString(),
                        label: form.name,
                    }));
                    setFormOptions(options);
                } else {
                    console.error("Failed to fetch forms or response is not as expected.");
                }
            } catch (error) {
                console.error("Error fetching forms:", error);
            }
        }
    };

    fetchForms();
}, [currentUser]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setNewTemplate({ ...newTemplate, [name]: value });
  };

  const handleFormChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const formId = selectedOption ? parseInt(selectedOption.value, 10) : undefined;
    setNewTemplate({ ...newTemplate, formId });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true); // Start loading
    if (currentUser !== null) {
      const authToken: string = await currentUser.getIdToken();
      const res = await updateFileTemplate(newTemplate, authToken);
      setLoading(false); // End loading
      if(res.success){
        toast.success(res.message);
        onSaveSuccess();
        setSelectedTemplate(undefined);
      }else{
        toast.error(res.message);
      }
    } else {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete the template "${newTemplate.fileName}"? This action cannot be undone.`)) {
      setLoading(true);
      if (currentUser !== null) {
        const authToken: string = await currentUser.getIdToken();
        const success = await deleteFileById(newTemplate.id.toString(), authToken);
        setLoading(false);
        
        if (success) {
          toast.success("Template deleted successfully");
          onSaveSuccess();
          setSelectedTemplate(undefined);
        } else {
          toast.error("Failed to delete template");
        }
      } else {
        setLoading(false);
        toast.error("User not authenticated");
      }
    }
  };

  return (
    <>
      <div className="common_card">
        <div className="top border_tittle">
          <div className="top_bar_tittle">
            <h2><button type="button" onClick={() => setSelectedTemplate(undefined)}><img style={{width: '1.5rem'}} src={backarrowbtn} alt="backarrowbtn" /></button> Edit Interim Template</h2>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="custom_input_flled">
            <label htmlFor="title">Filename</label>
            <input
              type="text"
              id="fileName"
              name="fileName"
              placeholder="Give your task a title..."
              value={newTemplate.fileName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="custom_input_flled">
            <label htmlFor="fileUpload">Estimated Time To Completion (in minutes)</label>
            <input
              id="estimatedTtc"
              type="number"
              name="estimatedTtc"
              min={0}
              value={newTemplate.estimatedTtc || 0}
              onChange={handleChange}
            />
          </div>
          <div className="custom_input_flled">
            <label htmlFor="formId">Form (Optional)</label>
            <Select
              id="formId"
              className="react-select-container"
              classNamePrefix="react-select"
              options={formOptions}
              onChange={handleFormChange}
              value={formOptions.find(
                (option) => option.value === newTemplate.formId?.toString()
              )}
              menuPosition="fixed"
              isClearable
            />
          </div>
         
          <div className="form_btn_flx mby_t_24">
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update Template"}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete} 
              disabled={loading}
              className="ms-2"
            >
              {loading ? "Deleting..." : "Delete Template"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditTemplateFile;